import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle, ArrowRight, HelpCircle, ChevronLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getExchangeLink, getFollowLink, getExchangeAffiliateLink } from "@/utils/exchangeUtils";
import { supabase } from "@/integrations/supabase/client";
import ExchangeVideoGuide from '@/components/follow-strategy/ExchangeVideoGuide';
import { motion } from "framer-motion";
import ChatOption from "@/components/ChatOption";
import { useIsMobile } from "@/hooks/use-mobile";

const ThankYouPremium = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const strategy = location.state?.strategy;
  const [isVerifying, setIsVerifying] = useState(true);
  const [exchangeLink, setExchangeLink] = useState("");
  const [isExchangeLinkLoading, setIsExchangeLinkLoading] = useState(true);
  const [affiliateLink, setAffiliateLink] = useState("");

  const isMobile = useIsMobile();

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        setIsVerifying(true);

        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          console.log("No authenticated user, redirecting to auth page");
          navigate("/auth");
          return;
        }

        const { data, error } = await supabase
          .from('strategy_subscriptions')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          console.error("Error checking subscription:", error);
        } else {
          const currentDate = new Date();
          const hasValidSubscription = data?.some(sub => {
            const nextBillingDate = sub.next_billing_date ? new Date(sub.next_billing_date) : null;
            return sub.active || (nextBillingDate && currentDate < nextBillingDate);
          });

          if (!hasValidSubscription) {
            console.log("No valid subscription found, redirecting to follow-strategy");
            navigate("/follow-strategy", {
              state: {
                strategy
              }
            });
            return;
          }
        }

        if (!strategy) {
          console.log("No strategy information, redirecting to marketplace");
          navigate("/marketplace");
          return;
        }

        if (strategy?.id) {
          try {
            const link = await getFollowLink(strategy.id);
            setExchangeLink(link);
          } catch (error) {
            console.error("Error loading exchange link:", error);
            setExchangeLink(getExchangeLink(strategy?.exchange || 'okx'));
          } finally {
            setIsExchangeLinkLoading(false);
          }
        } else {
          setExchangeLink(getExchangeLink(strategy?.exchange || 'okx'));
          setIsExchangeLinkLoading(false);
        }

        setAffiliateLink(getExchangeAffiliateLink(strategy?.exchange || 'okx'));
      } catch (error) {
        console.error("Error in verification:", error);
      } finally {
        setIsVerifying(false);
      }
    };

    verifySubscription();
  }, [strategy, navigate]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E7F0FD] to-white">
        <Header />
        <main className="container mx-auto px-4 py-16 flex flex-col items-center">
          <div className="flex flex-col items-center justify-center min-h-[50vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#517FA4]"></div>
            <p className="mt-4 text-[#517FA4]">Verifying your subscription...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleExchangeRedirect = () => {
    if (exchangeLink) {
      window.open(exchangeLink, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/*<div className="min-h-screen bg-gradient-to-b from-[#E7F0FD] to-white">*/}
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to="/marketplace"
          className="inline-flex items-center text-[#007BFF] hover:text-[#0056b3] mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Marketplace
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          <div className="flex justify-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="bg-green-100 p-4 rounded-full"
            >
              <Check className="w-12 h-12 text-green-600" />
            </motion.div>
          </div>

          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-gray-800">
              You Are Now Ready to Follow The Strategy on {strategy.exchange}!
            </h1>
            <p className="text-xl text-gray-600">
              Thanks to your Premium subscription, you now need to complete the setup on {strategy.exchange} to start copy trading. Once configured on the exchange, you'll receive all the signals from this expert strategy.
            </p>
          </div>

          <Card className="bg-[#F6C528]/10 border border-[#F6C528]/20 rounded-lg p-6 mb-6">
            <h2 className="font-semibold text-xl text-[#012846] mb-4">Premium Strategy Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <div>
                <p className="text-[#517FA4] text-sm">Strategy Name</p>
                <p className="text-[#243949] font-medium">{strategy.name}</p>
              </div>
              <div>
                <p className="text-[#517FA4] text-sm">Performance</p>
                <p className="text-[#243949] font-medium">{strategy.performance}</p>
              </div>
              <div>
                <p className="text-[#517FA4] text-sm">Min. Investment</p>
                <p className="text-[#243949] font-medium">{strategy.minInvestment}</p>
              </div>
              <div>
                <p className="text-[#517FA4] text-sm">Exchange</p>
                <p className="text-[#243949] font-medium">{strategy.exchange}</p>
              </div>
            </div>
          </Card>


          <div className="group bg-gradient-to-br from-[#FF3B3B] to-[#D72631] p-1 rounded-lg shadow-md">
            <Card className="p-6 bg-white rounded-md">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#FF3B3B]/10 flex items-center justify-center mr-4 group-hover:bg-[#FF3B3B]/20 transition-colors duration-300">
                  <HelpCircle className="w-7 h-7 text-[#FF3B3B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold group-hover:text-[#FF3B3B] transition-colors">
                    What Happens Next?
                  </h3>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-3">If you already have an {strategy.exchange} account:</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Log in to your exchange and confirm the copy-trading setup</li>
                    <li>Enable necessary permissions for premium strategy execution</li>
                    <li>Ensure sufficient balance for minimum investment requirements</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">If you're new to {strategy.exchange}:</h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>
                      <a
                        href={affiliateLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors duration-200"
                      >
                        Create a new account on {strategy?.exchange || "the exchange"}
                      </a>
                    </li>
                    <li>Complete the KYC verification process if required</li>
                    <li>Return to this page to activate copy trading</li>
                  </ul>
                </div>
              </div>


              <ExchangeVideoGuide exchange={strategy?.exchange || 'OKX'} />
            </Card>
          </div>


          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-4">Set Up Your Strategy on {strategy.exchange}</h2>
              <p className="text-gray-600 mb-6">
                Click below to set up copy trading for your selected premium strategy.
              </p>

              <div className="mt-8">
                <Button
                  onClick={handleExchangeRedirect}
                  className="bg-[#F6C528] hover:bg-[#F7D44C] text-[#012846] py-6 px-8 mb-8 text-lg"
                  disabled={isExchangeLinkLoading}
                >
                  {isExchangeLinkLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-[#012846] rounded-full"></span>
                      Loading...
                    </span>
                  ) : (
                    <>
                      Continue to {strategy.exchange}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-center space-y-4">
              <h3 className="font-semibold text-gray-500">Manage Your Strategies</h3>
              <p className="text-sm text-gray-500">
                Visit your Account page to manage your followed strategies and check performance.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/account", { state: { activeTab: "my-strategies" } })}
                className="bg-[#F6C528] text-[#012846] hover:bg-[#F7D44C] py-2 px-8"
              >
                View My Strategies
              </Button>
              <Button
                onClick={() => navigate("/marketplace")}
                variant="outline"
                className="border-[#517FA4] text-[#517FA4] hover:bg-[#517FA4]/10"
              >
                Explore More Strategies <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </motion.div>
      </main>
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}
      <Footer />
    </div>
  );
};

export default ThankYouPremium;
